import { createFileRoute } from '@tanstack/react-router';
import { CampaignBrandingCard, FinanceSetupCard } from '@/components/campaign';
import { SettingsSubPageHeader } from '@/components/settings';
import { useCampaignStore } from '@/stores/campaignStore';
import { useFinanceSettings } from '@/hooks/queries/useFinanceSettings';
import { readySettings } from '@/lib/financeSettingsRead';

export const Route = createFileRoute('/app/settings/campaign-committee')({
  component: CampaignCommitteePage,
});

/**
 * Campaign & Committee — the co-located "which door?" fix. Campaign identity
 * (branding) and the committee / compliance profile live under one section
 * heading: `CampaignBrandingCard` first, `FinanceSetupCard` below, each
 * unchanged and saved via its own existing endpoint (no merged form, no
 * dual-write).
 *
 * The election date is shown once: when a structured general election date
 * exists on finance settings, the branding card's free-text election-date input
 * is hidden.
 *
 * Section authz: the heading appears when the user can manage at least one of
 * TeamManagement or Finance. Each card already self-hides via its own guard
 * (branding → TeamManagement, committee → Finance), so a TeamManagement-only
 * user sees only branding and a Finance-only user sees only the committee card —
 * no second per-card gate is added here.
 */
export function CampaignCommitteePage() {
  const canManageTeam = useCampaignStore((s) => s.canManage('TeamManagement'));
  const canManageFinance = useCampaignStore((s) => s.canManage('Finance'));
  const financeSettings = readySettings(useFinanceSettings());

  // Any non-null GeneralDate counts (not Primary/Runoff specific).
  const hideElectionDate = financeSettings?.generalDate != null;

  if (!canManageTeam && !canManageFinance) return null;

  return (
    <div className="max-w-3xl p-6">
      <SettingsSubPageHeader
        title="Campaign & Committee"
        description="Your campaign identity and the committee / compliance profile, in one place."
      />

      <div className="space-y-4 mt-6">
        <CampaignBrandingCard hideElectionDate={hideElectionDate} />
        <FinanceSetupCard />
      </div>
    </div>
  );
}
